import { downloadCitation } from 'js/journals/downloadCitation';
import React, { useState } from 'react';
import { Icon } from 'js/design_system/Icon';
import { Tooltip } from 'js/design_system/new_tooltip/Tooltip';
import { TextButton } from 'js/design_system/TextButton';
import trackClick from 'js/utils/trackClick';

export function ArticleCitation({
  citations,
  publishedLetterId,
  workId,
  doi,
  canExport,
}: {
  citations: { name: string; citation: string }[];
  publishedLetterId: number;
  workId?: number;
  doi: string;
  canExport: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const citation = citations[0];
  const copy = async () => {
    setError('');
    try {
      await navigator.clipboard.writeText(citation.citation);
      setCopied(true);
      trackClick({
        source: 'journal_citation_copy',
        entityId: publishedLetterId,
        extra: {
          format: citation.name,
          work_id: workId,
          placement: 'article_footer',
        },
      });
    } catch {
      setError('Could not copy. Select the citation and copy it.');
    }
  };
  const exportCitation = async (format: 'ris' | 'bibtex') => {
    setExporting(true);
    setError('');
    try {
      await downloadCitation(doi, format);
      trackClick({
        source: 'journal_citation_export',
        entityId: publishedLetterId,
        extra: { format, work_id: workId, placement: 'article_footer' },
      });
    } catch {
      setError('Could not export the citation. Please try again.');
    } finally {
      setExporting(false);
    }
  };
  if (!citation) return null;
  return (
    <>
      <p>{citation.citation}</p>
      <div className="sap-evaluation__citation-actions">
        <TextButton
          inline
          icon={copied ? 'check' : 'content_copy'}
          onClick={copy}
        >
          {copied ? 'Copied' : 'Copy'}
        </TextButton>
        {canExport && (
          <>
            <TextButton
              inline
              icon="arrow_downward"
              disabled={exporting}
              aria-label="BibTeX"
              onClick={() => exportCitation('bibtex')}
            >
              BibTeX
            </TextButton>
            <TextButton
              inline
              className="sap-evaluation__ris-export"
              aria-label="RIS (EndNote, Mendeley, Zotero)"
              icon="arrow_downward"
              disabled={exporting}
              onClick={() => exportCitation('ris')}
            >
              RIS <span>(EndNote, Mendeley, Zotero)</span>
            </TextButton>
          </>
        )}
      </div>
      {error && <p role="alert">{error}</p>}
    </>
  );
}

export function CitationCountInfo() {
  const [open, setOpen] = useState(false);
  return (
    <Tooltip
      open={open}
      onChange={setOpen}
      placement="bottom-end"
      maxWidth={260}
      render={() => (
        <span id="sap-citation-count-info" role="tooltip">
          Citation counts are refreshed daily using Dimensions.
        </span>
      )}
    >
      <button
        type="button"
        className="sap-evaluation__citation-info"
        aria-label="About citation counts"
        aria-describedby={open ? 'sap-citation-count-info' : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false);
        }}
      >
        <Icon name="info" size={16} aria-hidden="true" />
      </button>
    </Tooltip>
  );
}
